import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';
import {
  STACK_COLORS,
  fmtCompact,
  fmtPct,
  getCurrencySymbol,
  buildStackChartData,
  buildSingleCcyChartData,
  buildAllTableRows,
  buildSingleCcyTableRows,
  aggregateCurrencyBreakdown,
} from '../../../utils/currencyDashboardUtils';
import type { MonthlyRecord } from '../../../hooks/useCurrencyDashboard';
import type { RawRow } from '../../../hooks/useCurrencyDashboard';
import type { RevenueAmountMode } from '../../../utils/currencyDashboardUtils';

interface RevenueTrendSectionProps {
  filtered: MonthlyRecord[];
  all: MonthlyRecord[];
  revenueMode: RevenueAmountMode;
}

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtAxis = (v: number): string => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

const fmtPctAxis = (v: number): string => `${v.toFixed(0)}%`;

// ─── Custom Tooltip for stacked bar ──────────────────────────────────────────
const StackedTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  topCcys?: string[];
  allDenom?: 'usd' | 'native';
}> = ({ active, payload, label, topCcys = [], allDenom = 'usd' }) => {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-[11px] font-bold text-slate-600 mb-2">{label}</p>
      {payload.map(p => {
        const symbol = allDenom === 'native' && topCcys.includes(p.name) ? getCurrencySymbol(p.name) : '$';
        return (
          <div key={p.name} className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: p.color }} />
              <span className="text-[11px] text-slate-600">{p.name}</span>
            </div>
            <span className="text-[11px] font-semibold tabular-nums">{fmtCompact(p.value, symbol)}</span>
          </div>
        );
      })}
      <div className="border-t border-slate-100 mt-2 pt-1 flex justify-between">
        <span className="text-[11px] text-slate-500">Total</span>
        <span className="text-[11px] font-bold tabular-nums">{fmtCompact(total)}</span>
      </div>
    </div>
  );
};

// ─── Single ccy tooltip ───────────────────────────────────────────────────────
const SingleTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  ccy: string;
}> = ({ active, payload, label, ccy }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-[11px] font-bold text-slate-600 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: p.color }} />
            <span className="text-[11px] text-slate-600">{p.name}</span>
          </div>
          <span className="text-[11px] font-semibold tabular-nums">
            {p.name === 'USD' ? fmtCompact(p.value) : `${fmtCompact(p.value, getCurrencySymbol(ccy))}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const RevenueTrendSection: React.FC<RevenueTrendSectionProps> = ({ filtered, all, revenueMode }) => {
  const breakdown = aggregateCurrencyBreakdown(filtered, revenueMode);
  const topCcys = breakdown.slice(0, 5).map(e => e.currency);
  const allCcys = breakdown.map(e => e.currency);

  const [selectedCcy, setSelectedCcy] = useState<'all' | string>('all');
  const [view, setView] = useState<'chart' | 'table'>('table');
  const [allDenom, setAllDenom] = useState<'usd' | 'native'>('native');

  const stackData = buildStackChartData(filtered, all, topCcys, allDenom, revenueMode);
  const singleData = selectedCcy !== 'all' ? buildSingleCcyChartData(filtered, all, selectedCcy, revenueMode) : [];

  const allTableRows = buildAllTableRows(filtered, all, topCcys, allDenom, revenueMode);

  const filteredRaw: RawRow[] = filtered.flatMap(m => m._currencyRows);
  const allRaw: RawRow[] = all.flatMap(m => m._currencyRows);
  const singleTableRows = selectedCcy !== 'all'
    ? buildSingleCcyTableRows(filteredRaw, allRaw, selectedCcy, revenueMode)
    : [];

  const isNotUsd = selectedCcy !== 'all' && selectedCcy !== 'USD';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Revenue Trend
          </span>
        </div>
        {/* View toggle */}
        <div className="flex items-center gap-1">
          {selectedCcy === 'all' && view === 'table' && (
            <div className="flex items-center gap-0.5 mr-2 bg-slate-100 rounded-md p-0.5">
              {(['usd', 'native'] as const).map(d => (
                <button
                  key={d}
                  onClick={() => setAllDenom(d)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                    allDenom === d ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  {d === 'usd' ? 'USD' : 'Native'}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
            {(['chart', 'table'] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors capitalize ${
                  view === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Currency selector */}
      <div className="px-4 py-2 border-b border-slate-50 flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setSelectedCcy('all')}
          className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            selectedCcy === 'all'
              ? 'bg-blue-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          ALL
        </button>
        {allCcys.map((ccy, idx) => (
          <button
            key={ccy}
            onClick={() => setSelectedCcy(ccy)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
              selectedCcy === ccy
                ? 'text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={selectedCcy === ccy ? { backgroundColor: STACK_COLORS[idx % STACK_COLORS.length] } : {}}
          >
            {ccy}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {view === 'chart' ? (
          <>
            {selectedCcy === 'all' ? (
              /* Stacked bar chart */
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stackData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={v => allDenom === 'native' ? `${v / 1000}K` : fmtAxis(v)}
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                    width={55}
                  />
                  <Tooltip content={<StackedTooltip topCcys={topCcys} allDenom={allDenom} />} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: '#64748B' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  {topCcys.map((ccy, idx) => (
                    <Bar
                      key={ccy}
                      dataKey={ccy}
                      stackId="a"
                      fill={STACK_COLORS[idx % STACK_COLORS.length]}
                      radius={idx === topCcys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              /* Area chart for single currency */
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={singleData} margin={{ top: 4, right: isNotUsd ? 50 : 8, left: 8, bottom: 4 }}>
                  <defs>
                    <linearGradient id="ccyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="usdGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tickFormatter={v => fmtCompact(v, getCurrencySymbol(selectedCcy))}
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  {isNotUsd && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={fmtAxis}
                      tick={{ fontSize: 10, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                      width={55}
                    />
                  )}
                  <Tooltip content={<SingleTooltip ccy={selectedCcy} />} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: '#64748B' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="amount"
                    name={selectedCcy}
                    stroke="#2563EB"
                    fill="url(#ccyGrad)"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  {isNotUsd && (
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="usd"
                      name="USD"
                      stroke="#059669"
                      fill="url(#usdGrad)"
                      strokeWidth={2}
                      strokeDasharray="4 2"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </>
        ) : (
          <>
            {selectedCcy === 'all' ? (
              /* ALL Table */
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] border-collapse min-w-[600px]">
                  <thead className="bg-slate-100 border-b-2 border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-left">Month</th>
                      {topCcys.map(ccy => (
                        <th key={ccy} className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">
                          {ccy}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">Total (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTableRows.map((row, idx) => (
                      <tr
                        key={row.ym}
                        className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}`}
                      >
                        <td className="px-3 py-2 font-medium text-slate-700">{row.month}</td>
                        {topCcys.map(ccy => {
                          const col = row.cols[ccy];
                          const pct = col?.momPct ?? null;
                          const symbol = allDenom === 'native' ? getCurrencySymbol(ccy) : '$';
                          return (
                            <td key={ccy} className="px-3 py-2 text-right">
                              <div className="tabular-nums font-medium text-slate-700">
                                {col && col.amount > 0 ? fmtCompact(col.amount, symbol) : '—'}
                              </div>
                              {pct != null && col && col.amount > 0 && (
                                <div className={`text-[10px] tabular-nums ${pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {fmtPct(pct, 1)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums">
                          {fmtCompact(row.grandUsd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Single CCY Table */
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] border-collapse min-w-[500px]">
                  <thead className="bg-slate-100 border-b-2 border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-left">Month</th>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">
                        Amount ({selectedCcy})
                      </th>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">USD</th>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">Share%</th>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">MoM%</th>
                      <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right">YoY%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {singleTableRows.map((row, idx) => (
                      <tr
                        key={row.ym}
                        className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}`}
                      >
                        <td className="px-3 py-2 font-medium text-slate-700">{row.month}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 font-medium">
                          {fmtCompact(row.local, getCurrencySymbol(selectedCcy))}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700 font-medium">
                          {fmtCompact(row.usd)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                          {row.sharePct != null ? `${row.sharePct.toFixed(1)}%` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${(row.momUsdPct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {fmtPct(row.momUsdPct)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${(row.yoyPct ?? 0) >= 0 ? 'text-violet-600' : 'text-orange-500'}`}>
                          {fmtPct(row.yoyPct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RevenueTrendSection;
